import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { useMemo } from 'react'

import type { PlacedOrder } from '@/bindings/PlacedOrder'
import type { TrackedSession } from '@/bindings/TrackedSession'
import { Badge } from '@/components/badge'
import { Page } from '@/components/layout'
import { orderTrackingPath, orderingKeys, sessionQuery } from '@/features/ordering/api'
import { useCustomerAuth } from '@/features/ordering/customer-auth'
import { CustomerHeader, CustomerLoadError, CustomerLoading, CustomerShell } from '@/features/ordering/customer-chrome'
import { ownsOrder } from '@/features/ordering/order-rules'
import { CancelOrderButton, LiveIndicator, OrderCard, SessionSheet } from '@/features/ordering/order-view'
import { useT } from '@/lib/i18n/translate'
import { branchEventsUrl, useBranchEvents } from '@/lib/live'
import { myOrderCodes } from '@/lib/my-orders'

const SESSION_ROUTE = '/$restaurantSlug/$branchSlug/s/$code'

/**
 * `/<r>/<b>/s/<code>` — the party's view of its dine-in session (decided
 * 2026-09-04, G7): everything ordered at the table by every phone, with
 * statuses and the running bill. Read-only across the party; cancel stays
 * per order, own orders only. Live through G5's hints, polling behind them.
 */
export function SessionTrackingPage() {
  const { restaurantSlug, branchSlug, code } = useParams({
    from: SESSION_ROUTE,
  })
  const t = useT()
  const auth = useCustomerAuth()
  const queryClient = useQueryClient()
  const query = useQuery(sessionQuery(restaurantSlug, branchSlug, code))
  const live = useBranchEvents(branchEventsUrl(restaurantSlug, branchSlug), () => {
    void queryClient.invalidateQueries({
      queryKey: orderingKeys.session(restaurantSlug, branchSlug, code),
    })
  })
  // "Yours" for an anonymous phone = the codes it placed (G6's list); logged in, the server says.
  const codesHeld = useMemo(() => new Set(myOrderCodes(restaurantSlug, branchSlug)), [restaurantSlug, branchSlug])

  if (query.isPending) return <CustomerLoading />
  if (!query.data) return <CustomerLoadError error={query.error} notFound={t('track.sessionNotFound')} retry={() => void query.refetch()} />

  const { restaurant, branch, session, orders, total } = query.data

  return (
    <CustomerShell color={restaurant.color}>
      <CustomerHeader restaurant={restaurant.name} branch={branch.name} auth={auth} />
      <Page width="sm" className="flex flex-1 flex-col gap-4">
        <SessionSheet session={session} currency={branch.currency} total={total} orders={orders}>
          {(order) => <SessionOrder restaurantSlug={restaurantSlug} branchSlug={branchSlug} data={query.data} order={order} mine={ownsOrder(order, codesHeld)} />}
        </SessionSheet>
        <LiveIndicator status={live} />
      </Page>
    </CustomerShell>
  )
}

/** One order at the table: a link to its own page, "Yours" when the phone placed it, cancel on an own live order. */
function SessionOrder({
  restaurantSlug,
  branchSlug,
  data,
  order,
  mine,
}: {
  restaurantSlug: string
  branchSlug: string
  data: TrackedSession
  order: PlacedOrder
  mine: boolean
}) {
  const t = useT()
  return (
    <OrderCard
      order={order}
      currency={data.branch.currency}
      main={data.restaurant.main_language}
      nested
      title={
        <Link to={orderTrackingPath(restaurantSlug, branchSlug, order.code)} className="text-accent-ink hover:underline">
          {t('track.title', { number: order.number })}
        </Link>
      }
      badges={mine && <Badge tone="success">{t('track.mine')}</Badge>}
      actions={mine && <CancelOrderButton restaurantSlug={restaurantSlug} branchSlug={branchSlug} order={order} />}
    />
  )
}
